import { useState } from "react";
import {
    Badge, Box, Button, Flex, Icon, IconButton, Menu, MenuButton, MenuList, MenuItem,
    Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import {
    MdAdd, MdStar, MdMoreVert, MdEdit, MdDeleteOutline, MdSwapHoriz,
} from "react-icons/md";
import AddMemberDialog from "../dialogs/AddMemberDialog";
import EditMemberDialog from "../dialogs/EditMemberDialog";
import RemoveMemberDialog from "../dialogs/RemoveMemberDialog";
import TransferLeadershipDialog from "../dialogs/TransferLeadershipDialog";
import { SectionTitle, EmptyState } from "../groupsUi";

/**
 * Renders the leader badge when applicable.
 * @param {{isLeader:boolean}} props
 */
function LeaderBadge({ isLeader }) {
    if (!isLeader) return null;
    return (
        <Badge display="inline-flex" alignItems="center" gap="4px" ml="8px"
            bg="rgba(255, 193, 7, 0.15)" color="rgba(255, 213, 79, 1)"
            border="1px solid" borderColor="rgba(255, 193, 7, 0.4)"
            borderRadius="full" px="8px" py="2px" fontSize="0.68rem" fontWeight="600" textTransform="none">
            <Icon as={MdStar} boxSize="12px" /> Leader
        </Badge>
    );
}

/**
 * Three-dot action menu for a member row.
 * @param {Object} props - { onEdit, onPromote, onRemove }
 */
function MemberRowMenu({ onEdit, onPromote, onRemove }) {
    return (
        <Menu placement="bottom-end">
            <MenuButton as={IconButton} aria-label="Actions" variant="ghost" size="sm" icon={<Icon as={MdMoreVert} />} />
            <MenuList>
                <MenuItem icon={<Icon as={MdEdit} />} onClick={onEdit}>Modifier</MenuItem>
                <MenuItem icon={<Icon as={MdSwapHoriz} />} onClick={onPromote}>Promouvoir leader</MenuItem>
                <MenuItem icon={<Icon as={MdDeleteOutline} />} color="red.400" onClick={onRemove}>Retirer</MenuItem>
            </MenuList>
        </Menu>
    );
}

/**
 * Returns the formatted payout cell content.
 * @param {Object} member
 * @returns {string}
 */
function formatPayout(member) {
    const v = member?.payout_percent;
    if (v === null || v === undefined) return "—";
    return `${Number(v).toFixed(1)}%`;
}

/**
 * Member identity cell (surname + leader badge + email).
 * @param {Object} props - { member }
 */
function MemberIdentityCell({ member }) {
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Flex align="center">
                <Text as="span" fontSize="0.82rem" fontWeight="600" color={nameColor}>{member.surname || "—"}</Text>
                <LeaderBadge isLeader={member.is_leader} />
            </Flex>
            <Text fontSize="0.72rem" color={subColor}>{member.email || "—"}</Text>
        </Box>
    );
}

/**
 * Single member row.
 * @param {Object} props - { member, onEdit, onPromote, onRemove }
 */
function MemberRow({ member, onEdit, onPromote, onRemove }) {
    return (
        <Tr>
            <Td><MemberIdentityCell member={member} /></Td>
            <Td>{member.role || "—"}</Td>
            <Td>{formatPayout(member)}</Td>
            <Td>{member.user_id ? `#${member.user_id}` : "—"}</Td>
            <Td textAlign="right"><MemberRowMenu onEdit={onEdit} onPromote={onPromote} onRemove={onRemove} /></Td>
        </Tr>
    );
}

/**
 * Members table (header + rows).
 * @param {Object} props - { members, onEdit, onPromote, onRemove }
 */
function MembersTable({ members, onEdit, onPromote, onRemove }) {
    return (
        <TableContainer>
            <Table size="sm">
                <Thead>
                    <Tr><Th>Membre</Th><Th>Rôle</Th><Th>Payout</Th><Th>User lié</Th><Th textAlign="right">Actions</Th></Tr>
                </Thead>
                <Tbody>
                    {members.map((m) => (
                        <MemberRow key={m.id} member={m}
                            onEdit={() => onEdit(m)} onPromote={() => onPromote(m)} onRemove={() => onRemove(m)} />
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

/**
 * Stateful members tab. Holds the dialog state for add/edit/remove/promote.
 * @param {Object} props - { detail, onRefresh, onChanged }
 */
function MembersTab({ detail, onRefresh, onChanged }) {
    const groupId = detail?.id;
    const members = detail?.members || [];
    const [addOpen, setAddOpen] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [removeMember, setRemoveMember] = useState(null);
    const [promoteMember, setPromoteMember] = useState(null);
    const onSuccess = (updated) => { onRefresh?.(); onChanged?.(updated); };

    return (
        <Stack spacing="16px">
            <Flex align="center" justify="space-between">
                <SectionTitle>Membres ({members.length})</SectionTitle>
                <Button size="sm" leftIcon={<Icon as={MdAdd} />} colorScheme="brand" variant="outline" onClick={() => setAddOpen(true)}>
                    Ajouter
                </Button>
            </Flex>
            {members.length === 0
                ? <EmptyState>Aucun membre dans ce groupe.</EmptyState>
                : <MembersTable members={members} onEdit={setEditMember} onPromote={setPromoteMember} onRemove={setRemoveMember} />}
            <AddMemberDialog open={addOpen} groupId={groupId} onClose={() => setAddOpen(false)} onAdded={(u) => { setAddOpen(false); onSuccess(u); }} />
            <EditMemberDialog member={editMember} onClose={() => setEditMember(null)} onUpdated={(u) => { setEditMember(null); onSuccess(u); }} />
            <RemoveMemberDialog member={removeMember} onClose={() => setRemoveMember(null)} onRemoved={() => { setRemoveMember(null); onSuccess(); }} />
            <TransferLeadershipDialog groupId={groupId} member={promoteMember} onClose={() => setPromoteMember(null)} onTransferred={() => { setPromoteMember(null); onSuccess(); }} />
        </Stack>
    );
}

export default MembersTab;
